import React from 'react';
import { Helmet } from 'react-helmet-async';
import PublicNav from 'layouts/PublicNav';
import PublicFooter from 'layouts/PublicFooter';
import useScrollReveal from 'hooks/useScrollReveal';
import './PricingPage.css';

const plans = [
  {
    id: 'open_source',
    name: 'Open Source',
    price: 'Free',
    priceNote: 'forever',
    desc: 'Get started with Zibby CLI — bring your own AI tokens',
    cta: 'Get started',
    ctaStyle: 'outline',
    sections: [
      {
        label: 'Included',
        rows: [
          { label: 'Zibby CLI runner', value: 'Included' },
          { label: 'Playwright script generation', value: 'Included' },
          { label: 'Local test execution', value: 'Unlimited' },
          { label: 'Your own AI tokens (Claude / Cursor)', value: 'Required' },
        ],
      },
      {
        label: 'Support',
        rows: [
          { label: 'Community / GitHub Issues', value: 'Included' },
        ],
      },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$49',
    priceNote: '/ mo',
    desc: 'Cloud-powered analysis, verifications, and integrations',
    highlight: true,
    cta: 'Get Pro',
    ctaStyle: 'solid',
    sections: [
      {
        label: 'Everything in Open Source, plus',
        rows: [
          { label: 'Cloud ticket analysis', value: '200 / mo' },
          { label: 'Video verifications', value: '100 / mo' },
          { label: 'Overage per analysis', value: '$0.50 / run' },
        ],
      },
      {
        label: 'Integrations',
        rows: [
          { label: 'Jira / GitLab / GitHub', value: 'Included' },
        ],
      },
      {
        label: 'Support',
        rows: [
          { label: 'Email support', value: 'Included' },
        ],
      },
      {
        label: 'Add-ons (optional)',
        rows: [
          { label: 'Dedicated egress IP', value: '+ $50 / mo' },
        ],
      },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    priceNote: '',
    desc: 'Unlimited analysis with dedicated support and custom integrations',
    cta: 'Contact',
    ctaStyle: 'outline',
    ctaHref: 'mailto:support@zibby.dev',
    sections: [
      {
        label: 'Everything in Pro, plus',
        rows: [
          { label: 'Cloud ticket analysis', value: 'Unlimited' },
          { label: 'Video verifications', value: 'Unlimited' },
        ],
      },
      {
        label: 'Integrations',
        rows: [
          { label: 'All standard integrations', value: 'Included' },
          { label: 'Custom integrations', value: 'Available' },
        ],
      },
      {
        label: 'Support',
        rows: [
          { label: 'Priority support', value: 'Included' },
        ],
      },
      {
        label: 'Add-ons (optional)',
        rows: [
          { label: 'Dedicated egress IP', value: '+ $50 / mo' },
        ],
      },
    ],
  },
];

const tools = [
  {
    title: 'Ticket analysis',
    desc: 'AI-powered analysis of tickets to generate implementation plans, test cases, and code review criteria automatically.',
  },
  {
    title: 'Code implementation',
    desc: 'Automated code generation and modification based on ticket requirements with built-in best practices.',
  },
  {
    title: 'Integration hub',
    desc: 'Connect with Jira, Linear, GitLab, GitHub, and more to streamline your development workflow.',
  },
  {
    title: 'Dedicated egress IP',
    desc: 'Optional Pro add-on. Get a static outbound IP for your account so you can allowlist Zibby on corporate GitLab / GitHub firewalls. $50 / mo, prorated.',
  },
];

const PricingPage = () => {
  const pageRef = useScrollReveal();

  return (
    <>
      <Helmet>
        <title>Pricing - Zibby</title>
        <meta name="description" content="Zibby Pricing - AI-powered software development platform pricing plans for teams of all sizes." />
      </Helmet>
      <div className="public-page fade-in" ref={pageRef}>
        <PublicNav />

        <div className="pricing-top">
        {/* Hero */}
        <div className="pricing-hero">
          <h1 className="reveal">Pricing</h1>
        </div>

        {/* Plans */}
        <div className="pricing-content">
          <div className="pricing-models-header reveal">
            <h2>Plans</h2>
            <div className="pricing-models-header-actions">
              <a href="mailto:support@zibby.dev" className="pricing-btn pricing-btn--outline">Contact</a>
              <a href="/connect" className="pricing-btn pricing-btn--solid">Start building</a>
            </div>
          </div>

          <div className="pricing-cards">
            {plans.map((plan, i) => (
              <div key={plan.name} className={`pricing-card${plan.highlight ? ' pricing-card--highlight' : ''} reveal${i > 0 ? ` reveal-delay-${i}` : ''}`}>
                <div>
                  <h3 className="pricing-card-title">{plan.name}</h3>
                  <div className="pricing-card-price">
                    <span className="pricing-card-price-value">{plan.price}</span>
                    {plan.priceNote && <span className="pricing-card-price-note">{plan.priceNote}</span>}
                  </div>
                  <p className="pricing-card-desc">{plan.desc}</p>
                </div>
                <a
                  href={plan.ctaHref || `/connect?returnTo=/billing&plan=${plan.id}`}
                  className={`pricing-card-cta pricing-card-cta--${plan.ctaStyle}`}
                >
                  {plan.cta}
                </a>
                {plan.sections.map((section) => (
                  <div key={section.label} className="pricing-table-section">
                    <div className="pricing-table-label">{section.label}</div>
                    {section.rows.map((row) => (
                      <div key={row.label} className="pricing-row">
                        <span className="pricing-row-label">{row.label}</span>
                        <span className="pricing-row-value">
                          {row.value}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>


          {/* Footnotes */}
          <div className="pricing-footnotes reveal">
            <p>Open source CLI and Playwright generation are free forever — bring your own AI tokens.</p>
            <p>Pro and Enterprise plans include cloud infrastructure, automatic updates, and data encryption at rest.</p>
            <a href="mailto:support@zibby.dev" className="pricing-footnotes-btn">Contact us for Enterprise pricing</a>
          </div>
        </div>
        </div>

        {/* Tools section */}
        <div className="pricing-tools">
          <div className="pricing-tools-inner">
            <h2 className="reveal">Pricing for tools</h2>
            <p className="pricing-tools-subtitle reveal reveal-delay-1">Get even more out of Zibby with advanced features and capabilities.</p>
            <div className="pricing-tools-cards">
              {tools.map((tool, i) => (
                <div key={tool.title} className={`pricing-tool-card reveal${i > 0 ? ` reveal-delay-${i}` : ''}`}>
                  <h3>{tool.title}</h3>
                  <p>{tool.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    </>
  );
};

export default PricingPage;
